<template>
  <div class="api-analytics">
    <div class="filters">
      <div class="filter-item">
        <label>From</label>
        <input v-model="filters.from" type="datetime-local" class="filter-input" />
      </div>
      <div class="filter-item">
        <label>To</label>
        <input v-model="filters.to" type="datetime-local" class="filter-input" />
      </div>
      <div class="filter-item">
        <label>Method</label>
        <select v-model="filters.method" class="filter-input">
          <option value="">ALL</option>
          <option value="GET">GET</option>
          <option value="POST">POST</option>
          <option value="PUT">PUT</option>
          <option value="PATCH">PATCH</option>
          <option value="DELETE">DELETE</option>
        </select>
      </div>
      <div class="filter-item">
        <label>Status class</label>
        <select v-model="filters.statusClass" class="filter-input">
          <option value="">ALL</option>
          <option value="2">2xx</option>
          <option value="3">3xx</option>
          <option value="4">4xx</option>
          <option value="5">5xx</option>
        </select>
      </div>
      <div class="filter-item route-filter">
        <label>Route</label>
        <input v-model="filters.route" placeholder="/api/..." class="filter-input" />
      </div>
      <button class="apply-btn" @click="loadAll" :disabled="loading">
        {{ loading ? 'Загрузка...' : 'Применить' }}
      </button>
    </div>

    <div class="kpis">
      <div class="kpi-card">
        <p class="kpi-title">Requests</p>
        <h3>{{ overview.totalHits || 0 }}</h3>
      </div>
      <div class="kpi-card">
        <p class="kpi-title">Error rate</p>
        <h3>{{ overview.errorRate || 0 }}%</h3>
      </div>
      <div class="kpi-card">
        <p class="kpi-title">P95</p>
        <h3>{{ overview.p95Ms || 0 }}ms</h3>
      </div>
    </div>

    <div class="chart-wrap">
      <Chart type="line" :data="chartData" :options="chartOptions" />
    </div>

    <div class="table-wrap">
      <h3>Top endpoints</h3>
      <table class="stats-table">
        <thead>
          <tr>
            <th @click="setSort('method')" class="sortable">Method {{ sortMark('method') }}</th>
            <th @click="setSort('route')" class="sortable">Route {{ sortMark('route') }}</th>
            <th @click="setSort('hits')" class="sortable">Hits {{ sortMark('hits') }}</th>
            <th @click="setSort('errors')" class="sortable">Errors {{ sortMark('errors') }}</th>
            <th @click="setSort('avg_ms')" class="sortable">Avg (ms) {{ sortMark('avg_ms') }}</th>
            <th @click="setSort('p95_ms')" class="sortable">P95 (ms) {{ sortMark('p95_ms') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="item in sortedTopEndpoints"
            :key="`${item.method}:${item.route}`"
            @click="selectEndpoint(item)"
            class="row-clickable"
          >
            <td>{{ item.method }}</td>
            <td>{{ item.route }}</td>
            <td>{{ item.hits }}</td>
            <td>{{ item.errors }}</td>
            <td>{{ item.avg_ms }}</td>
            <td>{{ item.p95_ms }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="endpointDetail" class="detail-wrap">
      <h3>Endpoint detail: {{ endpointDetail.method }} {{ endpointDetail.route }}</h3>
      <p>
        Hits: {{ endpointDetail.summary?.hits || 0 }} |
        Errors: {{ endpointDetail.summary?.errors || 0 }} |
        Avg: {{ endpointDetail.summary?.avg_ms || 0 }}ms |
        P95: {{ endpointDetail.summary?.p95_ms || 0 }}ms
      </p>
      <ul>
        <li v-for="status in endpointDetail.statusBreakdown" :key="status.status_class">
          {{ status.status_class }}xx: {{ status.hits }}
        </li>
      </ul>
    </div>

    <p v-if="error" class="error">{{ error }}</p>
  </div>
</template>

<script>
import Chart from 'primevue/chart';
import {
  getApiOverview,
  getApiTimeseries,
  getTopEndpoints,
  getEndpointDetail
} from '@/services/admin/apiAnalytics';
import { useUserStore } from '@/stores/user';

function toLocalInputDate(date) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export default {
  name: 'ApiAnalytics',
  components: { Chart },
  data() {
    const now = new Date();
    const prev = new Date(now.getTime() - 24 * 60 * 60 * 1000);
    return {
      loading: false,
      error: '',
      filters: {
        from: toLocalInputDate(prev),
        to: toLocalInputDate(now),
        method: '',
        statusClass: '',
        route: ''
      },
      overview: {},
      timeseries: [],
      topEndpoints: [],
      endpointDetail: null
      ,
      sortBy: 'route',
      sortDir: 'asc'
    };
  },
  computed: {
    userStore() {
      return useUserStore();
    },
    chartData() {
      return {
        labels: this.timeseries.map((p) => new Date(p.bucket).toLocaleString('ru-RU')),
        datasets: [
          {
            label: 'Hits',
            data: this.timeseries.map((p) => p.hits),
            borderColor: '#3b82f6',
            tension: 0.3
          },
          {
            label: 'Errors',
            data: this.timeseries.map((p) => p.errors),
            borderColor: '#ef4444',
            tension: 0.3
          },
          {
            label: 'P95 ms',
            data: this.timeseries.map((p) => p.p95_ms),
            borderColor: '#22c55e',
            tension: 0.3
          }
        ]
      };
    },
    chartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false
      };
    },
    sortedTopEndpoints() {
      const dir = this.sortDir === 'asc' ? 1 : -1;
      const list = [...this.topEndpoints];
      return list.sort((a, b) => {
        const key = this.sortBy;
        const av = a[key];
        const bv = b[key];
        if (typeof av === 'number' || typeof bv === 'number') {
          return ((Number(av) || 0) - (Number(bv) || 0)) * dir;
        }
        return String(av || '').localeCompare(String(bv || ''), 'ru') * dir;
      });
    }
  },
  async mounted() {
    await this.loadAll();
  },
  methods: {
    requestParams() {
      return {
        from: new Date(this.filters.from).toISOString(),
        to: new Date(this.filters.to).toISOString(),
        method: this.filters.method || undefined,
        statusClass: this.filters.statusClass || undefined,
        route: this.filters.route || undefined
      };
    },
    async loadAll() {
      this.loading = true;
      this.error = '';
      this.endpointDetail = null;
      try {
        const params = this.requestParams();
        const token = this.userStore?.token;
        const [overview, timeseries, top] = await Promise.all([
          getApiOverview(params, token),
          getApiTimeseries(params, token),
          getTopEndpoints({ ...params, sort: 'route', includeZero: 1, limit: 2000 }, token)
        ]);
        this.overview = overview;
        this.timeseries = timeseries.points || [];
        this.topEndpoints = top.items || [];
      } catch (err) {
        this.error = err.message || 'Ошибка загрузки аналитики API';
      } finally {
        this.loading = false;
      }
    },
    async selectEndpoint(item) {
      try {
        this.endpointDetail = await getEndpointDetail({
          ...this.requestParams(),
          route: item.route,
          method: item.method
        }, this.userStore?.token);
      } catch (err) {
        this.error = err.message || 'Ошибка загрузки детализации';
      }
    },
    setSort(column) {
      if (this.sortBy === column) {
        this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
        return;
      }
      this.sortBy = column;
      this.sortDir = column === 'route' || column === 'method' ? 'asc' : 'desc';
    },
    sortMark(column) {
      if (this.sortBy !== column) return '';
      return this.sortDir === 'asc' ? '▲' : '▼';
    }
  }
};
</script>

<style scoped>
.api-analytics {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
.filters {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 0.75rem;
  align-items: end;
}
.filter-item {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}
.route-filter {
  grid-column: span 2;
}
.filter-input {
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 0.5rem;
}
.apply-btn {
  height: 38px;
  border: none;
  border-radius: 8px;
  background: #111827;
  color: #fff;
  cursor: pointer;
}
.kpis {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.75rem;
}
.kpi-card {
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 0.8rem;
}
.kpi-title {
  margin: 0;
  color: #6b7280;
}
.chart-wrap {
  height: 320px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 0.5rem;
}
.table-wrap {
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 0.8rem;
}
.stats-table {
  width: 100%;
  border-collapse: collapse;
}
.stats-table th,
.stats-table td {
  text-align: left;
  padding: 0.55rem;
  border-bottom: 1px solid #f3f4f6;
}
.sortable {
  cursor: pointer;
  user-select: none;
}
.sortable:hover {
  background: #f9fafb;
}
.row-clickable {
  cursor: pointer;
}
.row-clickable:hover {
  background: #f9fafb;
}
.detail-wrap {
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 0.8rem;
}
.error {
  color: #dc2626;
}
@media (max-width: 1100px) {
  .filters {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .route-filter {
    grid-column: span 2;
  }
}
</style>
